import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock, LifeBuoy, Plus, Send, Wrench } from "lucide-react";
import { api, apiError, formatDate } from "@/lib/api";
import { emitChange } from "@/lib/events";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import SupportDialog from "@/components/SupportDialog";
import { EmptyState, LoadingPanel } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

const STATUS = {
  open: {
    label: "Waiting",
    icon: Clock,
    edge: "border-l-muted2",
    text: "text-muted2",
  },
  working: {
    label: "Being looked at",
    icon: Wrench,
    edge: "border-l-stock-low",
    text: "text-stock-low",
  },
  resolved: {
    label: "Sorted",
    icon: CheckCircle2,
    edge: "border-l-stock-in",
    text: "text-stock-in",
  },
};

function when(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const today = new Date().toDateString() === d.toDateString();
  const time = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return today ? time : `${formatDate(value)}, ${time}`;
}

function hasUnread(t) {
  const last = t.messages?.[t.messages.length - 1];
  if (!last?.from_support) return false;
  return !t.seen_at || last.at > t.seen_at;
}

export default function MyTickets() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [asking, setAsking] = useState(false);
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [activeId, setActiveId] = useState(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);

  const load = useCallback(
    async (selectId) => {
      try {
        const { data } = await api.get("/support/tickets/mine");
        setRows(data);
        setActiveId((current) => selectId || current || data[0]?.ticket_id || null);
        await api.post("/support/mine/seen").catch(() => {});
        emitChange();
      } catch (err) {
        toast.error(apiError(err));
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load();
    const timer = setInterval(() => load(), 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const active = rows.find((r) => r.ticket_id === activeId) || null;

  // Keep the newest message in view as the conversation grows.
  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [active?.messages?.length, activeId]);

  const closed = active?.status === "resolved";

  const send = async () => {
    if (!reply.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${active.ticket_id}/messages`, { body: reply });
      setReply("");
      await load(active.ticket_id);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const openCount = rows.filter((r) => r.status !== "resolved").length;

  return (
    <Layout
      title="Support"
      subtitle={
        // Held back until the data arrives — otherwise it briefly
        // describes an empty page.
        loading ? undefined : rows.length
          ? `${rows.length} message${rows.length === 1 ? "" : "s"}${
              openCount ? ` · ${openCount} still open` : ""
            }`
          : isOwner
          ? "This client hasn't sent anything yet"
          : "Anything not working, tell us here"
      }
      action={
        <Button onClick={() => setAsking(true)} className="bg-brand hover:bg-brand-hover">
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">New message</span>
        </Button>
      }
    >
      {loading ? (
        <LoadingPanel rows={3} />
      ) : rows.length === 0 ? (
        <div className="panel">
          <EmptyState
            icon={LifeBuoy}
            title="Nothing reported"
            body={
              isOwner
                ? "Nothing from this client yet. Your own messages to Anthropic aren't here — this shows what this business has sent you."
                : "If something isn't working or you're not sure how to do something, send a message and we'll come back to you here."
            }
            action={
              <Button onClick={() => setAsking(true)} className="bg-brand hover:bg-brand-hover">
                <Plus className="w-4 h-4 mr-2" /> New message
              </Button>
            }
          />
        </div>
      ) : (
        <div className="grid lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)] gap-4 items-start">
          {/* The list scrolls on its own so a long history doesn't move the
              conversation beside it. */}
          <div className="space-y-2 lg:max-h-[calc(100vh-210px)] lg:overflow-y-auto lg:pr-1">
            {rows.map((t) => {
              const meta = STATUS[t.status] || STATUS.open;
              const unread = hasUnread(t);
              const preview = t.messages?.[0]?.body || "";
              return (
                <button
                  key={t.ticket_id}
                  onClick={() => setActiveId(t.ticket_id)}
                  className={cn(
                    "w-full text-left rounded-xl border border-l-[3px] bg-white px-3.5 py-3 transition-colors",
                    meta.edge,
                    t.ticket_id === activeId
                      ? "border-brand bg-brand-light/60"
                      : "border-line hover:border-brand-ring"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "text-[11px] uppercase tracking-[0.06em] font-medium",
                        meta.text
                      )}
                    >
                      {meta.label}
                    </span>
                    {unread && (
                      <span className="ml-auto bg-brand text-white text-[10px] rounded-full px-2 py-0.5">
                        new
                      </span>
                    )}
                  </div>
                  <div className="text-[13px] text-ink mt-1.5 line-clamp-2 leading-snug">
                    {preview}
                  </div>
                  <div className="text-[11px] text-muted2 mt-1.5">
                    {formatDate(t.created_at)} · {t.urgency_label}
                  </div>
                </button>
              );
            })}
          </div>

          {active && (
            <div className="panel overflow-hidden flex flex-col min-w-0">
              <div className="px-5 py-3.5 border-b border-line flex items-center gap-2.5 flex-wrap">
                {(() => {
                  const meta = STATUS[active.status] || STATUS.open;
                  const Icon = meta.icon;
                  return (
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.06em] font-medium",
                        meta.text
                      )}
                    >
                      <Icon className="w-3.5 h-3.5" />
                      {meta.label}
                    </span>
                  );
                })()}
                <span className="text-[12.5px] text-muted2">{active.urgency_label}</span>
                {active.page && (
                  <span className="font-mono text-[11.5px] text-muted2">{active.page}</span>
                )}
              </div>

              <div ref={threadRef} className="flex-1 max-h-[380px] overflow-y-auto px-5 py-4 space-y-4">
                {active.messages?.map((m) => (
                  <div
                    key={m.message_id}
                    className={cn(
                      "max-w-[78%]",
                      m.from_support
                        ? "mr-auto border-l-2 border-line pl-3"
                        : "ml-auto border-r-2 border-brand pr-3 text-right"
                    )}
                  >
                    <div
                      className={cn(
                        "flex items-baseline gap-2",
                        !m.from_support && "justify-end"
                      )}
                    >
                      <span
                        className={cn(
                          "text-[12.5px] font-medium",
                          m.from_support ? "text-ink" : "text-brand"
                        )}
                      >
                        {m.from_support ? "Support" : "You"}
                      </span>
                      <span className="text-[11px] text-muted2">{when(m.at)}</span>
                    </div>
                    <div className="text-[13.5px] text-ink leading-relaxed whitespace-pre-wrap mt-1">
                      {m.body}
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-t border-line px-5 py-3.5">
                {closed ? (
                  <p className="text-[12.5px] text-muted2 text-center py-1">
                    This one's marked sorted.{" "}
                    <button
                      onClick={() => setAsking(true)}
                      className="link-quiet font-medium"
                    >
                      Send a new message
                    </button>{" "}
                    if you still need help.
                  </p>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          send();
                        }
                      }}
                      placeholder="Write a reply…"
                      className="flex-1"
                    />
                    <Button
                      onClick={send}
                      disabled={sending || !reply.trim()}
                      className="bg-brand hover:bg-brand-hover shrink-0"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <SupportDialog
        open={asking}
        onOpenChange={setAsking}
        onSent={(created) => load(created?.ticket_id)}
      />
    </Layout>
  );
}
